import type { TaskRun } from "../../../../shared/types";
import type { WorkflowContext, WorkflowStepModule } from "../types";

export type LanguageClaritySeverity = "low" | "medium" | "high";

export type LanguageClarityFlag = {
  phrase: string;
  issueType: string;
  severity: LanguageClaritySeverity;
  reason: string;
  suggestions: string[];
};

export type LanguageClaritySnapshot = {
  verdict: "clean" | "needs_edits";
  summary: string;
  flags: LanguageClarityFlag[];
  rewrittenText: string | null;
  surfaceNotes: string[];
};

function metadataRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

export function extractLanguageClaritySnapshot(
  run: TaskRun | null | undefined,
): LanguageClaritySnapshot | null {
  const output = metadataRecord(run?.output);
  if (!output) return null;
  const verdict = output.verdict;
  if (verdict !== "clean" && verdict !== "needs_edits") return null;

  const flags = Array.isArray(output.flags)
    ? output.flags.flatMap((item) => {
        const record = metadataRecord(item);
        if (!record) return [];
        const phrase = typeof record.phrase === "string" ? record.phrase.trim() : "";
        const issueType = typeof record.issue_type === "string" ? record.issue_type.trim() : "";
        const severity = record.severity;
        if (!phrase || !issueType || (severity !== "low" && severity !== "medium" && severity !== "high")) {
          return [];
        }
        return [{
          phrase,
          issueType,
          severity: severity as LanguageClaritySeverity,
          reason: typeof record.reason === "string" ? record.reason : "",
          suggestions: stringArray(record.suggestions),
        }];
      })
    : [];

  return {
    verdict,
    summary: typeof output.summary === "string" ? output.summary : "Language clarity review completed.",
    flags,
    rewrittenText: typeof output.rewritten_text === "string" ? output.rewritten_text : null,
    surfaceNotes: stringArray(output.surface_notes),
  };
}

export const languageClarityReviewStep: WorkflowStepModule<LanguageClaritySnapshot> = {
  id: "language_clarity_review",
  label: "Language clarity review",
  runButtonLabel: "Run clarity review",
  runningLabel: "Reviewing…",
  renderer: "clarity",
  readArtifacts: ["draft"],
  taskSlug: (context: WorkflowContext) => `language_clarity_review:${context.brief.id}`,
  isEligible: (context: WorkflowContext) =>
    context.draftText?.trim()
      ? { eligible: true }
      : { eligible: false, reason: "Requires a saved draft document." },
  buildParams: (context: WorkflowContext) => ({
    brief_id: context.brief.id,
    campaign_id: context.campaignId,
    surface: context.brief.surface ?? context.brief.platform ?? null,
    content_type: context.brief.format ?? null,
    draft_text: context.draftText ?? "",
    persona_hypothesis: context.brand?.strategy?.audience?.who ?? context.brand?.target_audience ?? null,
  }),
  extractSnapshot: extractLanguageClaritySnapshot,
};
